import styled, { css } from 'styled-components'

import {
  CSS_ELEMENTS_RESETS,
  media,
  SIZES,
  THEME,
  transformToREM,
  typeScale,
} from '../../styles'
import RecipeHeading from '../../components/RecipeHeading'
import RecipeActions from '../../components/RecipeActions'
import RecipeDifficultyIndicator from '../../components/RecipeDifficultyIndicator'
import RecipeConstituents from '../../components/RecipeConstituents'
import RecipeCharacteristicIndicators from '../../components/RecipeCharacteristicIndicators'
import RecipeFeedback from '../../components/RecipeFeedback'
import RecipeDirections from '../../components/RecipeDirections'
import RecipeTipCard from '../../components/RecipeTipCard'
import Grid from '../../components/Grid'
import RecipeCard from '../../components/RecipeCard'
import SpinnerLoader from '../../components/SpinnerLoader'

const Styled = styled.div`
  display: flex;
  flex-direction: column-reverse;
  overflow-anchor: none;
  ${({ preview }) => media.medium`
    overflow-anchor: unset;
    ${
      preview
        ? css`
            @media (orientation: landscape) {
              display: block;
            }
          `
        : ''
    };
  `}
`
Styled.mediaContainer = styled.div`
  position: relative;
  background: ${({ backgroundColor }) =>
    backgroundColor || THEME.COLORS.background.primary};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-flow: column nowrap;
  padding-top: 0;
  ${({ preview }) => media.medium`
    ${preview ? '@media (orientation: landscape) {' : ''}
    flex: 1;
    height: 100%;
    position: fixed;
    top: 0;
    padding-top: ${preview ? 0 : 'var(--top-offset)'};
    right: 0;
    bottom: 0;
    left: ${transformToREM(SIZES.recipe.aside.width.medium)};
    width: ${`calc(100% - ${transformToREM(SIZES.recipe.aside.width.medium)})`};
    ${preview ? '}' : ''}
  `}
`
Styled.difficulty = styled(RecipeDifficultyIndicator)`
  position: absolute;
  top: 1.2rem;
  right: 1.2rem;
  z-index: 1;
  ${media.medium`
    top: 1.6rem;
    left: 1.6rem;
    right: unset;
  `}
`
Styled.loader = styled(SpinnerLoader)`
  position: absolute;
  top: 50%;
  left: 50%;
`
Styled.side = styled.div`
  position: relative;
  background: ${THEME.COLORS.background.primary};
  width: 100%;
  ${({ preview }) => media.medium`
    width: ${transformToREM(SIZES.recipe.aside.width.medium)};
    height: 100%;
    overflow-y: auto;
    @media (orientation: portrait) {
      width: ${
        preview ? '100%' : transformToREM(SIZES.recipe.aside.width.medium)
      };
    }
  `}
`
Styled.scrollView = styled.div`
  &:last-child {
    /* Extra padding to prevent chat overlap */
    padding-bottom: 10rem;
  }
  ${media.medium`
    &:last-child {
      padding-bottom: 0;
    }
  `}
`
Styled.header = styled.header`
  background-color: ${THEME.COLORS.background.primary};

  ${({ preview }) => media.medium`
      background-color: ${
        preview
          ? THEME.COLORS.background.primary
          : THEME.COLORS.background.secondary
      };
  `};
`
Styled.heading = styled(RecipeHeading)``
Styled.tags = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  justify-content: center;
  width: 100%;
  background-color: ${({ backgroundColor }) =>
    backgroundColor || THEME.COLORS.background.primary};
  padding: 1.6rem 2.4rem 0;
  ${media.medium`
    padding: 1.6rem 3.2rem 0;
  `}
`
Styled.tag = styled.span`
  ${CSS_ELEMENTS_RESETS.button};
  ${typeScale('button', { fontSize: '1.4rem', lineHeight: '1.4rem' })};
  display: inline-block;
  background-color: ${THEME.COLORS.background.primary};
  color: ${THEME.COLORS.foreground.primary};
  border-radius: 9999px;
  margin: 0 0.4rem 0.4rem;
  padding: 0.8rem 1.2rem;
`
Styled.subtitle = styled.h2`
  ${typeScale('button')};
  padding-bottom: ${({ hasCaption }) => (hasCaption ? '0.4rem' : '1.6rem')};
  padding-right: ${({ hasPicker }) => (hasPicker ? '7.2rem' : '0')};
  padding-left: ${({ hasPicker }) => (hasPicker ? '7.2rem' : '0')};
  text-align: center;
  text-transform: lowercase;

  &:first-letter {
    text-transform: uppercase;
  }
`
Styled.description = styled.p`
  white-space: pre-wrap;
`
Styled.captionTitle = styled.p`
  color: ${THEME.COLORS.foreground.secondary};
  padding-bottom: 1.6rem;
`
Styled.characteristicsIndicators = styled(RecipeCharacteristicIndicators)``
Styled.covers = styled.div`
  position: absolute;
  top: 2.4rem;
  right: 2.4rem;
`
Styled.constituents = styled(RecipeConstituents)``
Styled.details = styled.section`
  background-color: ${THEME.COLORS.background.secondary};
`
Styled.section = styled.div`
  padding: 2.4rem;
  position: relative;
  display: block;
  background: ${({ backgroundColor }) =>
    backgroundColor || THEME.COLORS.background.secondary};
  border-top: ${({ noBorder }) =>
    noBorder ? 0 : `0.1rem dashed ${THEME.COLORS.foreground.tertiary}`};

  &:first-of-type {
    border-top: 0;
  }
`
Styled.actions = styled(RecipeActions)`
  background-color: none;
`
Styled.recipeFeedbackSection = styled(RecipeFeedback)`
  background-color: ${THEME.COLORS.background.secondary};
  border-top: ${({ noBorder }) =>
    noBorder ? 0 : `0.1rem dashed ${THEME.COLORS.foreground.tertiary}`};

  &:first-of-type {
    border-top: 0;
  }
`
Styled.directions = styled(RecipeDirections)``
Styled.tipCard = styled(RecipeTipCard)`
  margin-top: 1.6rem;
`
Styled.tools = styled.p`
  text-transform: lowercase;

  &:first-letter {
    text-transform: capitalize;
  }
`
Styled.similarRecipeList = styled(Grid)`
  position: relative;
  margin: 0 auto;
  width: 100%;
`
Styled.similarRecipeListItem = styled.div`
  position: relative;
  width: auto;
`
Styled.recipeCard = styled(RecipeCard)`
  height: 100%;
`

export default Styled
